import { useEffect } from "react";
import { CLS_META } from "../lib/constants";
import { relativeTime, hasKorean, pickKoreanTitle, cleanText } from "../lib/utils";

const DIR_COLORS = {
  up:   "text-dir-escalation",
  down: "text-dir-deescalation",
};
const dirLabel = d => d === "up" ? "확전 요인" : d === "down" ? "진정 요인" : "중립";

export default function ArticleDetail({ article, onClose, onOverride }) {
  useEffect(() => {
    if (!article) return;
    window.history.pushState({ articleDetail: true }, "");
    const handlePop = () => onClose();
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [article, onClose]);

  if (!article) return null;

  const { hed, hed_ko, src, publishedAt, time, dir, cls, reasoning, summary, summary_ko, altSources, url } = article;
  // gun/watershed는 summary_ko(한글) + summary(영어 원문) 둘 다 표시 — 검증용.
  // 그 외는 영어 원문만.
  const isHighTier = cls === "gun" || cls === "watershed";
  const excerptKo = isHighTier && summary_ko ? cleanText(summary_ko) : "";
  const excerptEn = cleanText(summary);
  const displayTime = publishedAt ? relativeTime(publishedAt) : time;
  const cm = CLS_META[cls] || CLS_META.common;
  const showHITL = cls === "gun" || cls === "watershed" || cls === "notable";

  return (
    <div className="fixed inset-0 z-50 bg-bg-primary overflow-y-auto animate-fadeUp">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-bg-primary border-b border-border-line px-4 py-3 flex items-center">
        <button
          onClick={onClose}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center
            bg-transparent border-none text-text-secondary text-headline cursor-pointer"
          aria-label="뒤로가기"
        >←</button>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* 출처 · 시간 */}
        <div className="flex gap-1.5 items-center flex-wrap mb-2">
          <span className="text-[11px] font-semibold text-text-secondary">{src}</span>
          {altSources?.map((s, i) => (
            <span key={i} className="text-[11px] text-text-tertiary font-medium">+{s.src}</span>
          ))}
          <span className="text-meta text-text-tertiary">· {displayTime}</span>
        </div>

        {/* 한국어 헤드라인 */}
        <div className="text-headline font-bold text-text-primary leading-snug mb-1">
          {cleanText(pickKoreanTitle(hed_ko, hed))}
        </div>

        {/* 영문 헤드라인 — 한글 번역이 실제로 존재할 때만 */}
        {hasKorean(hed_ko) && (
          <div className="text-sub text-text-tertiary leading-snug mb-3">
            {cleanText(hed)}
          </div>
        )}

        {/* 방향 · Tier · HITL */}
        <div className="flex items-center gap-2 mb-4">
          <span className={`text-meta font-bold tracking-wide uppercase ${DIR_COLORS[dir] || "text-text-tertiary"}`}>
            {dirLabel(dir)}
          </span>
          {cls && (
            <span className="text-meta font-bold tracking-wide uppercase" style={{ color: cm.color }}>
              {cm.label}
            </span>
          )}
          {showHITL && onOverride && (
            <span className="ml-auto flex gap-0 flex-shrink-0">
              <button
                onClick={() => onOverride(article, "up")}
                className="min-w-[40px] min-h-[40px] flex items-center justify-center
                  bg-transparent border-none text-text-tertiary text-sub cursor-pointer hover:text-text-primary"
                title="등급 상향"
              >↑</button>
              <button
                onClick={() => onOverride(article, "down")}
                className="min-w-[40px] min-h-[40px] flex items-center justify-center
                  bg-transparent border-none text-text-tertiary text-sub cursor-pointer hover:text-text-primary"
                title="등급 하향"
              >↓</button>
            </span>
          )}
        </div>

        {/* Why it matters */}
        {reasoning && (
          <div className="mb-4 pt-4 border-t border-border-line">
            <div className="text-meta font-bold tracking-widest uppercase text-text-tertiary mb-2">Why it matters</div>
            <div className="text-body text-text-secondary leading-relaxed">
              {reasoning}
            </div>
          </div>
        )}

        {/* What it says — gun/watershed는 한글 번역. 그 외는 영어 원문. */}
        {(excerptKo || excerptEn.length >= 10) && (
          <div className="mb-4 pt-4 border-t border-border-line">
            <div className="text-meta font-bold tracking-widest uppercase text-text-tertiary mb-2">What it says</div>
            <div className="text-body text-text-secondary leading-relaxed">
              {excerptKo || excerptEn}
            </div>
          </div>
        )}

        {/* Original — gun/watershed에서 한글 번역이 있을 때만 영어 원문 별도 표시 (검증용) */}
        {excerptKo && excerptEn.length >= 10 && (
          <div className="mb-4 pt-4 border-t border-border-line">
            <div className="text-meta font-bold tracking-widest uppercase text-text-tertiary mb-2">Original</div>
            <div className="text-body text-text-secondary leading-relaxed">
              {excerptEn}
            </div>
          </div>
        )}

        {/* 원문 보기 */}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block p-3 bg-bg-secondary border border-border-line text-center text-sub font-bold text-text-primary tracking-wide no-underline min-h-[44px] flex items-center justify-center"
          >
            원문 보기 →
          </a>
        )}
      </div>
    </div>
  );
}
